import type { ReactNode } from "react";
import type { MultiplayerGame } from "src/core/types";

type Props = {
  game: MultiplayerGame | null;
  playerId: string | null;
  currentPlayer: number;
};

const ConnectionStatus = ({ game, playerId, currentPlayer }: Props): ReactNode => {
  if (!game) return null;

  const hostPlayer = game.players[game.host];
  const guestPlayer = game.guest ? game.players[game.guest] : null;

  const isHost = playerId === game.host;
  const opponent = isHost ? guestPlayer : hostPlayer;

  // Determine player's side (0 = top/blue, 1 = bottom/pink)
  const playerSide = isHost ? 0 : 1;
  const playerColor = playerSide === 0 ? "Blue (Top)" : "Pink (Bottom)";

  // Determine whose turn it is using local state (updates after CHANGE_PLAYER action)
  const isYourTurn = currentPlayer === playerSide;
  const turnText = isYourTurn ? "Your turn" : "Opponent's turn";

  return (
    <div
      data-testid="connection-status"
      css={(theme) => ({
        position: "absolute",
        top: 10,
        right: 10,
        background: "rgba(255, 255, 255, 0.95)",
        padding: "12px 16px",
        borderRadius: 6,
        fontSize: "13px",
        color: theme.colors.grey5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        zIndex: 100,
        minWidth: 200
      })}
    >
      <div css={{ marginBottom: 8, fontWeight: "bold", fontSize: "14px" }} data-testid="room-code">
        <strong>Room Code:</strong> {game.roomCode}
      </div>

      <div css={{ marginBottom: 6 }} data-testid="player-info">
        <strong>You:</strong> {isHost ? "Host" : "Guest"} • {playerColor}
      </div>

      <div
        data-testid="turn-indicator"
        css={{
          marginBottom: 8,
          padding: "4px 8px",
          background: isYourTurn ? "#e3f2fd" : "#f5f5f5",
          borderRadius: 4,
          fontWeight: "bold",
          color: isYourTurn ? "#1976d2" : "#666"
        }}
      >
        {turnText}
      </div>

      {opponent ? (
        <div css={{ display: "flex", alignItems: "center", gap: 6 }} data-testid="opponent-status">
          <div
            css={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: opponent.connected ? "#4caf50" : "#f44336"
            }}
          />
          <span>Opponent: {opponent.connected ? "Connected" : "Disconnected"}</span>
        </div>
      ) : (
        <div css={{ color: "#ff9800", fontWeight: 500 }} data-testid="waiting-status">
          Waiting for opponent to join...
        </div>
      )}
    </div>
  );
};

export default ConnectionStatus;
